<script setup lang="ts">
// todo:ai-review
import type { SQLiteExampleNode } from "#layers/books/types"

const props = defineProps<{
  node: SQLiteExampleNode
  bookId: string
}>()

// Fetch SQL content from the book's raw file API
const { data: setupSql, pending: setupPending } = props.node.setupFile
  ? useFetch<string>(`/api/books/${props.bookId}/raw/${props.node.setupFile}`)
  : { data: ref(null), pending: ref(false) }

const { data: querySql, pending: queryPending } = useFetch<string>(
  `/api/books/${props.bookId}/raw/${props.node.queryFile}`
)

const isLoading = computed(() => setupPending.value || queryPending.value)
</script>

<template>
  <div class="sqlite-example">
    <!-- Title -->
    <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-4">
      {{ node.title }}
    </h2>

    <!-- Preamble (markdown content from +sqljsexample.md) -->
    <div
      v-if="node.preamble"
      class="prose prose-gray dark:prose-invert max-w-none mb-6"
    >
      <p>{{ node.preamble }}</p>
    </div>

    <!-- Loading state -->
    <div v-if="isLoading" class="flex items-center justify-center py-8">
      <UIcon name="i-heroicons-arrow-path" class="w-6 h-6 animate-spin text-gray-400" />
      <span class="ml-2 text-gray-500">Loading SQL...</span>
    </div>

    <template v-else>
      <!-- Setup SQL (optional) -->
      <div v-if="setupSql" class="mb-6">
        <h3 class="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2">
          Setup
        </h3>
        <pre class="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm"><code class="language-sql">{{ setupSql }}</code></pre>
      </div>

      <!-- Query SQL -->
      <div v-if="querySql" class="mb-6">
        <h3 class="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2">
          Query
        </h3>
        <pre class="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm"><code class="language-sql">{{ querySql }}</code></pre>
      </div>

      <!-- Placeholder for future interactive execution -->
      <div class="mt-4 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg border border-dashed border-gray-300 dark:border-gray-600">
        <p class="text-sm text-gray-500 dark:text-gray-400 text-center">
          Interactive SQL execution coming soon
        </p>
      </div>
    </template>
  </div>
</template>

<style scoped>
.sqlite-example pre {
  margin: 0;
}

.sqlite-example code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}
</style>
